import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createService,
  deleteService,
  fetchServices,
  updateService,
  updateServiceStatus,
} from '@/features/services/api'
import type { Service, ServiceEditorFormData, ServiceStatus } from '@/features/services/types'

export const servicesQueryKeys = {
  all: ['services'] as const,
  detail: (id?: string) => ['services', 'detail', id] as const,
}

export function useServicesList() {
  return useQuery({
    queryKey: servicesQueryKeys.all,
    queryFn: fetchServices,
  })
}

export function useServiceStatusMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ServiceStatus }) =>
      updateServiceStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: servicesQueryKeys.all })
    },
  })
}

export function useServiceDeleteMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteService(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: servicesQueryKeys.all })
    },
  })
}

interface UseServiceSaveMutationOptions {
  service?: Service | null
  onSuccess?: (service: Service) => void
  onError?: (error: unknown) => void
}

export function useServiceSaveMutation(options: UseServiceSaveMutationOptions = {}) {
  const queryClient = useQueryClient()
  const { service, onSuccess, onError } = options

  return useMutation({
    mutationFn: (data: ServiceEditorFormData) => {
      if (service?.id) {
        return updateService(service.id, data)
      }
      return createService(data)
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: servicesQueryKeys.all })
      onSuccess?.(result)
    },
    onError,
  })
}
